import type { Locale } from './common.js'
import { type MoneyValue, money, toMoney } from './money.js'

/**
 * An OMR amount written out in words, as receipt and payment vouchers print it (sales-documents Q10,
 * ASM-092): whole rials, then the baisa (1 rial = 1,000 baisa), then "only".
 *
 *   en  150.500 → "One hundred and fifty Omani rials and five hundred baisa only"
 *   ar  150.500 → «فقط مائة وخمسون ريالاً عمانياً وخمسمائة بيسة لا غير»
 *
 * The amount is first rounded to the baisa (3 decimal places, half away from zero), so 1.2345 reads as
 * 1.235. English uses British wording ("one hundred and five", "one thousand and five"). Arabic follows
 * the counted-noun rules: one and two after the noun (ريال عماني واحد، ريالان عمانيان), 3–10 with the
 * plural and reversed gender (ثلاثة ريالات، ثلاث بيسات), 11–99 with the singular accusative (أحد عشر
 * ريالاً، إحدى عشرة بيسة), round hundreds and above with the singular genitive, and a dual before the
 * noun in the construct form (مائتا ريال، ألفا ريال).
 *
 * Negative amounts and amounts of a quadrillion rials or more are refused (RangeError): a voucher
 * prints a positive sum, and money fields hold at most 15 integer digits.
 */
export function amountInWords(amount: MoneyValue, lang: Locale): string {
  const value = money(toMoney(amount))
  if (value.isNegative() && !value.isZero()) throw new RangeError('A negative amount has no voucher wording')
  const rials = value.trunc()
  if (rials.greaterThan(MAX_RIALS)) throw new RangeError('The amount is too large to write out')
  const baisa = value.minus(rials).times(1000).toNumber()
  return lang === 'ar' ? arabic(rials.toNumber(), baisa) : english(rials.toNumber(), baisa)
}

const MAX_RIALS = 999_999_999_999_999

// ── English ──────────────────────────────────────────────────────────────────

const EN_ONES = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
]
const EN_TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety']
const EN_SCALES = ['', 'thousand', 'million', 'billion', 'trillion']

function enUnder100(n: number): string {
  if (n < 20) return EN_ONES[n] as string
  const unit = n % 10
  return `${EN_TENS[Math.floor(n / 10)]}${unit ? `-${EN_ONES[unit]}` : ''}`
}

function enUnder1000(n: number): string {
  const hundreds = Math.floor(n / 100)
  const rest = n % 100
  if (!hundreds) return enUnder100(rest)
  return `${EN_ONES[hundreds]} hundred${rest ? ` and ${enUnder100(rest)}` : ''}`
}

/** A whole number in English words ("one thousand two hundred and thirty-four"). */
function enInteger(n: number): string {
  if (n === 0) return 'zero'
  const words: string[] = []
  const groups = splitGroups(n)
  for (let i = groups.length - 1; i >= 0; i--) {
    const g = groups[i] as number
    if (!g) continue
    let part = enUnder1000(g)
    if (i > 0) part += ` ${EN_SCALES[i]}`
    // "one thousand and five": a last group under a hundred after higher ones takes "and".
    else if (g < 100 && words.length) part = `and ${part}`
    words.push(part)
  }
  return words.join(' ')
}

function english(rials: number, baisa: number): string {
  const parts: string[] = []
  if (rials) parts.push(`${enInteger(rials)} Omani ${rials === 1 ? 'rial' : 'rials'}`)
  if (baisa) parts.push(`${enInteger(baisa)} baisa`)
  const text = `${parts.length ? parts.join(' and ') : 'zero Omani rials'} only`
  return text.charAt(0).toUpperCase() + text.slice(1)
}

// ── Arabic ───────────────────────────────────────────────────────────────────

type Gender = 'm' | 'f'

/** 1–10 before a masculine noun (3–10 take the ة: ثلاثة ريالات) and before a feminine one. */
const AR_ONES: Record<Gender, readonly string[]> = {
  m: ['', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة', 'عشرة'],
  f: ['', 'واحدة', 'اثنتان', 'ثلاث', 'أربع', 'خمس', 'ست', 'سبع', 'ثماني', 'تسع', 'عشر'],
}
const AR_TENS = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون']
const AR_HUNDREDS = [
  '',
  'مائة',
  'مائتان',
  'ثلاثمائة',
  'أربعمائة',
  'خمسمائة',
  'ستمائة',
  'سبعمائة',
  'ثمانمائة',
  'تسعمائة',
]

/** A counted noun's forms: after 1 (it precedes «واحد»), 2 (dual), 3–10, 11–99, and round numbers. */
interface Noun {
  gender: Gender
  one: string
  two: string
  plural: string
  accusative: string
  genitive: string
}

/** The scale nouns (all masculine). */
const AR_SCALES: readonly (Noun | null)[] = [
  null,
  { gender: 'm', one: 'ألف', two: 'ألفان', plural: 'آلاف', accusative: 'ألفاً', genitive: 'ألف' },
  {
    gender: 'm',
    one: 'مليون',
    two: 'مليونان',
    plural: 'ملايين',
    accusative: 'مليوناً',
    genitive: 'مليون',
  },
  {
    gender: 'm',
    one: 'مليار',
    two: 'ملياران',
    plural: 'مليارات',
    accusative: 'ملياراً',
    genitive: 'مليار',
  },
  {
    gender: 'm',
    one: 'تريليون',
    two: 'تريليونان',
    plural: 'تريليونات',
    accusative: 'تريليوناً',
    genitive: 'تريليون',
  },
]

const RIAL: Noun = {
  gender: 'm',
  one: 'ريال عماني',
  two: 'ريالان عمانيان',
  plural: 'ريالات عمانية',
  accusative: 'ريالاً عمانياً',
  genitive: 'ريال عماني',
}

const BAISA: Noun = {
  gender: 'f',
  one: 'بيسة',
  two: 'بيستان',
  plural: 'بيسات',
  accusative: 'بيسة',
  genitive: 'بيسة',
}

function arUnder100(n: number, gender: Gender): string {
  if (n <= 10) return AR_ONES[gender][n] as string
  if (n === 11) return gender === 'm' ? 'أحد عشر' : 'إحدى عشرة'
  if (n === 12) return gender === 'm' ? 'اثنا عشر' : 'اثنتا عشرة'
  if (n < 20) return `${AR_ONES[gender][n - 10]} ${gender === 'm' ? 'عشر' : 'عشرة'}`
  const unit = n % 10
  const tens = AR_TENS[Math.floor(n / 10)] as string
  if (!unit) return tens
  const unitWord = unit === 1 ? (gender === 'm' ? 'واحد' : 'إحدى') : (AR_ONES[gender][unit] as string)
  return `${unitWord} و${tens}`
}

function arUnder1000(n: number, gender: Gender): string {
  const hundreds = Math.floor(n / 100)
  const rest = n % 100
  const parts: string[] = []
  if (hundreds) parts.push(AR_HUNDREDS[hundreds] as string)
  if (rest) parts.push(arUnder100(rest, gender))
  return parts.join(' و')
}

/**
 * A number directly before a genitive noun takes the construct form: a dual loses its ن (مائتا ريال،
 * ألفا ريال) and an accusative scale noun its tanween (أحد عشر ألف ريال).
 */
function construct(phrase: string): string {
  for (const dual of ['مائتان', 'ألفان', 'مليونان', 'ملياران', 'تريليونان']) {
    if (phrase.endsWith(dual)) return phrase.slice(0, -1)
  }
  for (const scale of AR_SCALES) {
    if (scale && phrase.endsWith(scale.accusative)) {
      return phrase.slice(0, -scale.accusative.length) + scale.genitive
    }
  }
  return phrase
}

/** `n` of `noun`, with the number–noun agreement described on `amountInWords`. */
function arCounted(n: number, noun: Noun, words: (n: number, gender: Gender) => string): string {
  if (n === 1) return `${noun.one} ${noun.gender === 'm' ? 'واحد' : 'واحدة'}`
  if (n === 2) return noun.two
  const rest = n % 100
  if (rest >= 3 && rest <= 10) return `${words(n, noun.gender)} ${noun.plural}`
  if (rest >= 11) return `${words(n, noun.gender)} ${noun.accusative}`
  return `${construct(words(n, noun.gender))} ${noun.genitive}`
}

/** A whole number in Arabic words, agreeing with a counted noun of `gender`. */
function arInteger(n: number, gender: Gender): string {
  if (n === 0) return 'صفر'
  const groups = splitGroups(n)
  const parts: string[] = []
  for (let i = groups.length - 1; i >= 0; i--) {
    const g = groups[i] as number
    if (!g) continue
    const scale = AR_SCALES[i]
    if (!scale) parts.push(arUnder1000(g, gender))
    // A group counts its (masculine) scale noun; one thousand is «ألف» alone, not «ألف واحد».
    else parts.push(g === 1 ? scale.one : arCounted(g, scale, arUnder1000))
  }
  return parts.join(' و')
}

function arabic(rials: number, baisa: number): string {
  const parts: string[] = []
  if (rials) parts.push(arCounted(rials, RIAL, arInteger))
  if (baisa) parts.push(arCounted(baisa, BAISA, arInteger))
  return `فقط ${parts.length ? parts.join(' و') : `صفر ${RIAL.genitive}`} لا غير`
}

// ── shared ───────────────────────────────────────────────────────────────────

/** Groups of three digits, least significant first (1234567 → [567, 234, 1]). */
function splitGroups(n: number): number[] {
  const groups: number[] = []
  let rest = n
  while (rest > 0) {
    groups.push(rest % 1000)
    rest = Math.floor(rest / 1000)
  }
  return groups
}
